import React from 'react';
import PropTypes from 'prop-types';
import compose from 'lodash.flowright';
import { graphql } from 'react-apollo';
import { connect } from 'react-redux';

import { MeQuery } from '../../../../graphql/queries/account';
import usePurchaseEventCheckout from '../PurchaseEvent/hooks/usePurchaseEventCheckout';
import { getErrorString } from '../../../../util/normalize-errors';
import useOpenAlert from '../../../../hooks/useOpenAlert';
import Spinner from '../../../../components/global/Spinner';
import { STRIPE_CHECKOUT_MODAL } from '../../../../components/global/StripeCheckout';
import { formatPriceForDisplay } from '../../../../util/format-money';
import { ACCOUNT_REFERRAL_TYPE } from '../../../../constants/account-referrals';
import useOpenModal from '../../../../hooks/useOpenModal';
import WeddingEventComparison from '../WeddingEventPurchase/components/WeddingEventComparison';
import { isWeddingsContent } from '../../../../util/album';

const propTypes = {
  user: PropTypes.shape({
    id: PropTypes.string,
  }),
  albumFolderId: PropTypes.string.isRequired,
  onCheckoutSuccess: PropTypes.func,
};

const WeddingEventUpgrade = ({ user, albumFolderId, onCheckoutSuccess }) => {
  const openAlert = useOpenAlert();

  const {
    eventItems,
    loadingEventItems,
    loadingPurchasedEvents,
    onCheckout,
    purchasedEvents,
    submitting,
  } = usePurchaseEventCheckout({
    albumFolderId,
    account: user,
    onCheckoutSuccess: ({ eventItem }) => {
      onCheckoutSuccess?.(eventItem);
    },
    onCheckoutError: error => {
      openAlert({
        message: getErrorString(error),
        info: error?.request_id || '',
      });
    },
  });

  const openModal = useOpenModal();

  const weddingEventItems = eventItems.filter(eventItem =>
    isWeddingsContent(eventItem),
  );

  if (
    loadingEventItems ||
    !weddingEventItems?.length ||
    // We don't want to block during refetch.
    (!purchasedEvents && loadingPurchasedEvents)
  ) {
    return (
      <div className="p-5 d-flex">
        <Spinner className="m-auto" />
      </div>
    );
  }

  const activePurchasedEvent = purchasedEvents?.records?.[0];

  const onPurchase = ({ eventItem, skipPayment }) => {
    if (skipPayment) {
      onCheckout({
        eventItemId: eventItem.id,
      });
      return;
    }

    openModal(STRIPE_CHECKOUT_MODAL, {
      buttonLabel: `Pay $${formatPriceForDisplay(eventItem?.price.value)}`,
      // If the event is FREE, will not require CC
      cardRequired: false,
      onSuccess: token =>
        onCheckout({
          eventItemId: eventItem.id,
          token,
        }),
      checkoutPriceVariables: ({ promo_code: promoCode }) => {
        return {
          event_items: [
            {
              event_item_id: eventItem.id,
              quantity: 1,
              album_folder_id: albumFolderId,
            },
          ],
          promo_code: promoCode,
        };
      },
      accountReferralTypes: [
        ACCOUNT_REFERRAL_TYPE.WEDDINGS_50_USD_OFF,
        ACCOUNT_REFERRAL_TYPE.WEDDINGS_100_PC_OFF_ONCE,
        ACCOUNT_REFERRAL_TYPE.WEDDINGS,
      ],
      defaultName: user?.name ?? '',
      defaultEmail: user?.email_address ?? '',
    });
  };

  if (!activePurchasedEvent) {
    return (
      <div className="d-flex p-5">
        <Spinner className="m-auto" />
      </div>
    );
  }

  return (
    <>
      {submitting && (
        <div className="d-flex flex-column justify-content-center align-items-center my-2">
          <span className="text-primary mb-2">
            Processing your request&hellip;
          </span>
          <Spinner size="sm" className="m-auto " />
        </div>
      )}
      <div className="wedding-event-comparison-container">
        <WeddingEventComparison
          weddingEventItems={weddingEventItems}
          onSelectPlan={onPurchase}
          activePurchasedEvent={activePurchasedEvent}
        />
      </div>
      <p className="text-faded mt-2 text-center">
        *Dedicated storage is valid for 365 days
      </p>
    </>
  );
};

const mapStateToProps = ({ auth }) => ({
  authenticated: auth.authenticated,
});

const withUserData = graphql(MeQuery, {
  props: ({ data: { me } }) => ({
    user: me,
  }),
  skip: ({ authenticated }) => !authenticated,
});

WeddingEventUpgrade.propTypes = propTypes;

export default compose(
  connect(mapStateToProps),
  withUserData,
)(WeddingEventUpgrade);
